
import React, { useCallback, useEffect, useState } from "react";
import ProductModal from "./ProductModal";
import Accordion from "./common/Accordion";
import { AgGridReact } from "ag-grid-react";
import { NavLink } from "react-router-dom";
import {
  fetchContactDetailsData,
  getFormattedDate,
} from "../../../../actions/common";
import ExtendedFreeTrial from "./ExtendedFreeTrial";
import ProductOverviewSkeleton from "./skeleton/ProductOverviewSkeleton";
import NoDataFound from "./common/NoDataFound";
import ConfirmationModal from "./common/ConfirmationModal";
import toast from "react-hot-toast";
import { apiRequest } from "../../../../utils/utils";
import { getCookie } from "../../customer/Utility";

const colors = {
  success: "#28A745",
  secondary: "#6C757D",
  primary: "#007BFF",
  danger: "#DC3545",
  warning: "#FFC107",
  info: "#17A2B8",
  light: "#F8F9FA",
  dark: "#343A40",
};

function hexToRgba(hex, opacity) {
  const colorHex =
    colors[hex] || (hex && hex.startsWith("#") ? hex : "#6C757D");
  const cleanHex = colorHex.replace("#", "");

  let r = parseInt(cleanHex.substring(0, 2), 16) || 108;
  let g = parseInt(cleanHex.substring(2, 4), 16) || 117;
  let b = parseInt(cleanHex.substring(4, 6), 16) || 125;

  return {
    color: colorHex,
    backgroundColor: `rgba(${r},${g},${b},${opacity})`,
    borderColor: `rgba(${r},${g},${b},0.5)`,
  };
}

const OverviewPanel = ({
  productsList,
  customerDetails,
  ticketList,
  columnsDef,
  usersList,
  subscriptionList,
  infraDetails,
  loading,
  fetchSubscriptionDetails,
  userTokens,
  appDetails,
  ismuvi5,
}) => {
  const [showExtendedTrialModal, setShowExtendedTrialModal] = useState(false);
  const [extendedTrialData, setExtendedTrialData] = useState({});
  const [showSwitchToDemoModal, setShowSwitchToDemoModal] = useState(false);
  const [switchAccountData, setSwitchAccountData] = useState(null);
  const [askSales, setAskSales] = useState([]);
  const [deals, setDeals] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [liveChat, setLiveChat] = useState([]);
  const [contactUs, setContactUs] = useState([]);

  useEffect(() => {
    if (!customerDetails || !customerDetails.mautic_id) return;

    const fetchParallelData = async () => {
      const results = await Promise.allSettled([
        fetchContactDetailsData(
          "/api/crm/contact/ask-sales",
          customerDetails.mautic_id,
        ),
        fetchContactDetailsData(
          "/api/crm/contact/deals",
          customerDetails.mautic_id,
        ),
        fetchContactDetailsData(
          "/api/crm/contact/email-statistics",
          customerDetails.mautic_id,
        ),
        fetchContactDetailsData(
          "/api/crm/contact/live-chat",
          customerDetails.mautic_id,
        ),
         fetchContactDetailsData(
          "/api/crm/contact/contact-us",
          customerDetails.mautic_id,
        ),
      ]);

      const askSalesRes = results[0];
      const dealsRes = results[1];
      const emailStatsRes = results[2];
      const liveChatRes = results[3];
      const contactUsRes = results[4];

      if (askSalesRes.status === "fulfilled" && askSalesRes.value) {
        setAskSales(askSalesRes.value.data || []);
      }

      if (dealsRes.status === "fulfilled" && dealsRes.value) {
        setDeals(dealsRes.value.data || []);
      }

      if (emailStatsRes.status === "fulfilled" && emailStatsRes.value) {
        setEmailStats(emailStatsRes.value.data || null);
      }

      if (liveChatRes.status === "fulfilled" && liveChatRes.value) {
        setLiveChat(liveChatRes.value.data || []);
      }
      if (contactUsRes.status === "fulfilled" && contactUsRes.value) {
        setContactUs(contactUsRes.value.data || []);
      }
    };

    fetchParallelData();
  }, [customerDetails.mautic_id]);

  let user_uuid = getCookie("muvi_useruuid");

  let muvi5prod = {};
  if (
    ismuvi5 &&
    Array.isArray(productsList) &&
    customerDetails.product &&
    Array.isArray(customerDetails.product)
  ) {
    const product = productsList.find(
      (prod) => prod.product_key === customerDetails.product[0],
    );
    console.log(product, "muvi555");
    const product_name = product ? product.value : "";

    muvi5prod = {
      product_name,
      billing_period: "NA",
      account_status:
        customerDetails.account_type.charAt(0).toUpperCase() +
          customerDetails.account_type.slice(1) || "NA",
      plan_name: appDetails.package_name || customerDetails.package || "NA",
      plan_duration: "",
      applications: customerDetails.applications || [],
    };
  }

  const handleAccountSwitch = (productKey, is_demo) => {
    const payload = {
      app_token: customerDetails.customer_id || "",
      is_demo: is_demo,
      is_live: false,
      product_key: productKey || "",
      user_uuid: customerDetails.customer_user_uuid || "",
      muvi_useruuid: user_uuid || "",
      mautic_id: customerDetails.mautic_id || "",
      store_key: productKey === "3D28C83C85544F0D9517DD2E7C7EEDAA" ? customerDetails.store_key : "",
    };
    setShowSwitchToDemoModal(true);
    setSwitchAccountData(payload);
  };

  const handleConfirmAccountSwitch = async () => {
    setShowSwitchToDemoModal(false);
    const responseData = await apiRequest(
      `${process.env.REACT_APP_CRM}/muvi6/manage-demo-account`,
      "POST",
      switchAccountData,
    );

    if (responseData.code === 200) {
      toast.success(
        `Account successfully Moved to ${switchAccountData.is_demo ? "Demo" : "Lead"
        }.`,
        {
          id: 1,
          position: "top-right",
        }
      );
      fetchSubscriptionDetails();
    } else {
      toast.error(responseData.message, { id: 1, position: "top-right" });
    }
  };

  const getProductName = useCallback(
    (productKey) => {
      const product = productsList.find(
        (prod) => prod.product_key === productKey,
      );
      return product ? product.value : "NA";
    },
    [productsList],
  );

  const HighlightPill = ({ label, value, icon }) => (
    <div className="card highlight-pill">
      <span className="highlight-pill-icon">{icon}</span>
      <div className="highlight-pill-text">
        <span className="highlight-pill-text-label">{label}</span>
        <span className="highlight-pill-text-value">{value}</span>
      </div>
    </div>
  );

  function convertUnixTimeStampToDate(unixTimestamp) {
    if (unixTimestamp) {
      const date = new Date(unixTimestamp * 1000).toLocaleDateString("en-CA");
      return date;
    }
  }
  return (
    <div role="tabpanel" class="tab-pane active" id="overview">
      <div className="flex-col gap-12">
        <Accordion header={"Highlights"} targetID={`highlightAccordion`}>
          <div className="flex-row gap-16">
            <div className="highlight-section">
              <h5>Lead Data Highlights</h5>
              <div className="highlight-pills-container flex-row flex-wrap gap-8">
                <HighlightPill
                  label="Email Sent"
                  value={(emailStats && emailStats.sent_count) || "0"}
                  icon={
                    <svg
                      fill="#000000"
                      height="16"
                      width="16"
                      version="1.1"
                      id="Capa_1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 485.638 485.638"
                    >
                      <g>
                        <path
                          d="M472.689,156.996L328.642,12.949c-17.266-17.266-45.353-17.266-62.616,0L12.948,266.024
		                        c-17.264,17.266-17.264,45.353,0,62.617L156.995,472.69c17.266,17.264,45.354,17.265,62.617,0l253.076-253.076
		                        C489.954,202.348,489.952,174.26,472.689,156.996z M183.546,455.21c-2.064-0.707-4.031-1.745-5.68-3.393L33.819,307.771
		                        c-0.039-0.041-0.048-0.092-0.09-0.133l149.816-0.001V455.21z M42.601,278.118L278.122,42.596l0.002,229.085
		                        c-0.002,3.444-2.998,6.442-6.443,6.442L42.601,278.118z M213.06,437.498v-129.86h58.623c9.609,0,18.64-3.741,25.427-10.528
		                        c6.788-6.788,10.527-15.817,10.528-25.428V213.06l129.854,0.005L213.06,437.498z M307.637,183.545V33.729
		                        c0.04,0.041,0.093,0.05,0.134,0.091l144.047,144.047c1.646,1.647,2.685,3.613,3.388,5.683L307.637,183.545z"
                        />
                        <path
                          d="M14.382,62.57h151.154c7.941,0,14.381-6.438,14.381-14.38c0-7.942-6.439-14.381-14.381-14.381H14.382
		                        c-7.943,0-14.381,6.438-14.381,14.381C0.001,56.132,6.439,62.57,14.382,62.57z"
                        />
                        <path
                          d="M14.382,119.065h94.784c7.942,0,14.38-6.438,14.38-14.381c0-7.941-6.438-14.38-14.38-14.38H14.382
		                        c-7.943,0-14.381,6.438-14.381,14.38C0.001,112.627,6.439,119.065,14.382,119.065z"
                        />
                        <path
                          d="M14.382,173.849h61.197c7.941,0,14.381-6.438,14.381-14.381s-6.439-14.381-14.381-14.381H14.382
		                        c-7.943,0-14.381,6.439-14.381,14.381S6.439,173.849,14.382,173.849z"
                        />
                        <path
                          d="M75.579,438.278H14.382c-7.943,0-14.381,6.439-14.381,14.381c0,7.942,6.438,14.381,14.381,14.381h61.197
		                        c7.941,0,14.381-6.438,14.381-14.381C89.96,444.717,83.521,438.278,75.579,438.278z"
                        />
                      </g>
                    </svg>
                  }
                />
                <HighlightPill
                  label="Lead Score"
                  value={(customerDetails && customerDetails.lead_score) || "0"}
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          d="M11.245 4.174C11.4765 3.50808 11.5922 3.17513 11.7634 3.08285C11.9115 3.00298 12.0898 3.00298 12.238 3.08285C12.4091 3.17513 12.5248 3.50808 12.7563 4.174L14.2866 8.57639C14.3525 8.76592 14.3854 8.86068 14.4448 8.93125C14.4972 8.99359 14.5641 9.04218 14.6396 9.07278C14.725 9.10743 14.8253 9.10947 15.0259 9.11356L19.6857 9.20852C20.3906 9.22288 20.743 9.23007 20.8837 9.36432C21.0054 9.48051 21.0605 9.65014 21.0303 9.81569C20.9955 10.007 20.7146 10.2199 20.1528 10.6459L16.4387 13.4616C16.2788 13.5829 16.1989 13.6435 16.1501 13.7217C16.107 13.7909 16.0815 13.8695 16.0757 13.9507C16.0692 14.0427 16.0982 14.1387 16.1563 14.3308L17.506 18.7919C17.7101 19.4667 17.8122 19.8041 17.728 19.9793C17.6551 20.131 17.5108 20.2358 17.344 20.2583C17.1513 20.2842 16.862 20.0829 16.2833 19.6802L12.4576 17.0181C12.2929 16.9035 12.2106 16.8462 12.1211 16.8239C12.042 16.8043 11.9593 16.8043 11.8803 16.8239C11.7908 16.8462 11.7084 16.9035 11.5437 17.0181L7.71805 19.6802C7.13937 20.0829 6.85003 20.2842 6.65733 20.2583C6.49056 20.2358 6.34626 20.131 6.27337 19.9793C6.18915 19.8041 6.29123 19.4667 6.49538 18.7919L7.84503 14.3308C7.90313 14.1387 7.93218 14.0427 7.92564 13.9507C7.91986 13.8695 7.89432 13.7909 7.85123 13.7217C7.80246 13.6435 7.72251 13.5829 7.56262 13.4616L3.84858 10.6459C3.28678 10.2199 3.00588 10.007 2.97101 9.81569C2.94082 9.65014 2.99594 9.48051 3.11767 9.36432C3.25831 9.23007 3.61074 9.22289 4.31559 9.20852L8.9754 9.11356C9.176 9.10947 9.27631 9.10743 9.36177 9.07278C9.43726 9.04218 9.50414 8.99359 9.55657 8.93125C9.61593 8.86068 9.64887 8.76592 9.71475 8.57639L11.245 4.174Z"
                          stroke="#000000"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </g>
                    </svg>
                  }
                />
                <HighlightPill
                  label="Open Rate"
                  value={
                    (emailStats &&
                      emailStats.open_rate &&
                      (emailStats.open_rate * 100).toFixed(2) + "%") ||
                    "0%"
                  }
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          d="M15.0007 12C15.0007 13.6569 13.6576 15 12.0007 15C10.3439 15 9.00073 13.6569 9.00073 12C9.00073 10.3431 10.3439 9 12.0007 9C13.6576 9 15.0007 10.3431 15.0007 12Z"
                          stroke="#000000"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                        <path
                          d="M12.0012 5C7.52354 5 3.73326 7.94288 2.45898 12C3.73324 16.0571 7.52354 19 12.0012 19C16.4788 19 20.2691 16.0571 21.5434 12C20.2691 7.94291 16.4788 5 12.0012 5Z"
                          stroke="#000000"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </g>
                    </svg>
                  }
                />
                <HighlightPill
                  label="Click Rate"
                  value={
                    (emailStats &&
                      emailStats.click_through_rate &&
                      (emailStats.click_through_rate * 100).toFixed(2) + "%") ||
                    "0%"
                  }
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          d="M7 7L5.5 5.5M15 7L16.5 5.5M5.5 16.5L7 15M11 5L11 3M5 11L3 11M17.1603 16.9887L21.0519 15.4659C21.4758 15.3001 21.4756 14.7003 21.0517 14.5346L11.6992 10.8799C11.2933 10.7213 10.8929 11.1217 11.0515 11.5276L14.7062 20.8801C14.8719 21.304 15.4717 21.3042 15.6375 20.8803L17.1603 16.9887Z"
                          stroke="#000000"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </g>
                    </svg>
                  }
                />
              </div>
            </div>
            <div className="separator-vertical"></div>
            <div className="highlight-section">
              <h5>Usage Metrics</h5>
              <div className="highlight-pills-container flex-row flex-wrap gap-8">
                <HighlightPill
                  label="Logins"
                  value="0"
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          d="M2.00098 11.999L16.001 11.999M16.001 11.999L12.501 8.99902M16.001 11.999L12.501 14.999"
                          stroke="#000000"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                        <path
                          d="M9.00195 7C9.01406 4.82497 9.11051 3.64706 9.87889 2.87868C10.7576 2 12.1718 2 15.0002 2L16.0002 2C18.8286 2 20.2429 2 21.1215 2.87868C22.0002 3.75736 22.0002 5.17157 22.0002 8L22.0002 16C22.0002 18.8284 22.0002 20.2426 21.1215 21.1213C20.2429 22 18.8286 22 16.0002 22H15.0002C12.1718 22 10.7576 22 9.87889 21.1213C9.11051 20.3529 9.01406 19.175 9.00195 17"
                          stroke="#000000"
                          stroke-width="1.5"
                          stroke-linecap="round"
                        ></path>
                      </g>
                    </svg>
                  }
                />
                <HighlightPill
                  label="Contents"
                  value="0"
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          d="M3 15C3 17.8284 3 19.2426 3.87868 20.1213C4.75736 21 6.17157 21 9 21H15C17.8284 21 19.2426 21 20.1213 20.1213C21 19.2426 21 17.8284 21 15"
                          stroke="#000000"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                        <path
                          d="M12 16V3M12 3L16 7.375M12 3L8 7.375"
                          stroke="#000000"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </g>
                    </svg>
                  }
                />
                <HighlightPill
                  label="Bandwidth"
                  value={
                    infraDetails &&
                    infraDetails.bandwidth &&
                    infraDetails.bandwidth.consumed != null
                      ? `${infraDetails.bandwidth.consumed}`
                      : "0 GB"
                  }
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M5.94601 5.59492C3.92853 4.15983 1 5.48359 1 7.83062V16.1694C1 18.5164 3.92853 19.8402 5.94601 18.4051L11 14.81V16.1694C11 18.5164 13.9285 19.8402 15.946 18.4051L21.8074 14.2357C23.3975 13.1046 23.3975 10.8954 21.8074 9.76429L15.946 5.59492C13.9285 4.15983 11 5.48359 11 7.83062V9.18996L5.94601 5.59492ZM3.0462 7.83062C3.0462 7.04828 4.02237 6.60703 4.69487 7.08539L10.5563 11.2548C11.0863 11.6318 11.0863 12.3682 10.5563 12.7452L4.69487 16.9146C4.02237 17.393 3.0462 16.9517 3.0462 16.1694V7.83062ZM13.0462 7.83062C13.0462 7.04828 14.0224 6.60703 14.6949 7.08539L20.5563 11.2548C21.0863 11.6318 21.0863 12.3682 20.5563 12.7452L14.6949 16.9146C14.0224 17.393 13.0462 16.9517 13.0462 16.1694V7.83062Z"
                          fill="#000000"
                        ></path>
                      </g>
                    </svg>
                  }
                />
                <HighlightPill
                  label="Storage"
                  value={
                    infraDetails &&
                    infraDetails.storage &&
                    infraDetails.storage.consumed != null
                      ? `${infraDetails.storage.consumed}`
                      : appDetails && appDetails.storage
                        ? appDetails.storage
                        : "0 GB"
                  }
                  icon={
                    <svg
                      width="16"
                      height="16"
                      fill="#000000"
                      viewBox="0 0 35 35"
                      data-name="Layer 2"
                      id="f3cb8d82-36fc-45b3-81de-6922a15d3e7e"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path d="M17.5,16.7a2,2,0,1,1,2-2A2,2,0,0,1,17.5,16.7Zm0-2h0Z"></path>
                        <path d="M28.559,34.75H6.441A4.2,4.2,0,0,1,2.25,30.559V4.442A4.2,4.2,0,0,1,6.441.25H28.559A4.2,4.2,0,0,1,32.75,4.442V30.559A4.2,4.2,0,0,1,28.559,34.75ZM6.441,2.75A1.694,1.694,0,0,0,4.75,4.442V30.559A1.693,1.693,0,0,0,6.441,32.25H28.559a1.693,1.693,0,0,0,1.691-1.691V4.442A1.694,1.694,0,0,0,28.559,2.75Z"></path>
                        <path d="M17.46,23.445a8.75,8.75,0,1,1,8.75-8.75A8.759,8.759,0,0,1,17.46,23.445Zm0-15a6.25,6.25,0,1,0,6.25,6.25A6.257,6.257,0,0,0,17.46,8.445Z"></path>
                        <path d="M8.2,7.45a1.251,1.251,0,0,0,0-2.5,1.251,1.251,0,0,0,0,2.5Z"></path>
                        <path d="M26.8,7.45a1.251,1.251,0,0,0,0-2.5,1.251,1.251,0,0,0,0,2.5Z"></path>
                        <path d="M8.2,29.67a1.251,1.251,0,0,0,0-2.5,1.251,1.251,0,0,0,0,2.5Z"></path>
                        <path d="M26.8,29.67a1.251,1.251,0,0,0,0-2.5,1.251,1.251,0,0,0,0,2.5Z"></path>
                        <path d="M10.46,24.31a1.251,1.251,0,0,1-.89-2.128l4.5-4.56a1.25,1.25,0,0,1,1.78,1.756l-4.5,4.56A1.246,1.246,0,0,1,10.46,24.31Z"></path>
                      </g>
                    </svg>
                  }
                />
              </div>
            </div>
          </div>
        </Accordion>
        {/* Product Overview */}
        {
          customerDetails && customerDetails.customer_id && userTokens && (

            <Accordion header={"Product Overview"} targetID="productAccordion">
              <div className="product-list scrollable scrollable-x">
                {loading ? (
                  <ProductOverviewSkeleton />
                ) : (subscriptionList &&
                  Array.isArray(subscriptionList) &&
                  subscriptionList.length === 0) ||
                !Array.isArray(subscriptionList) ? (
                <NoDataFound />
              ) : (
                subscriptionList &&
                subscriptionList &&
                subscriptionList.map((product, index) => (
                  <ProductModal
                    key={product.product_key || index}
                    product={product}
                    productKey={product.product_key}
                    // productName={product.product_name}
                    customerDetails={customerDetails}
                    setExtendedTrialData={setExtendedTrialData}
                    setShowExtendedTrialModal={setShowExtendedTrialModal}
                    handleAccountSwitch={handleAccountSwitch}
                    ismuvi5={ismuvi5}
                  />
                ))
              )}
            </div>
          </Accordion>
        )}
        {customerDetails && ismuvi5 && (
          <Accordion header={"Product Overview"} targetID="productAccordion">
            <div className="product-list scrollable scrollable-x">
              <ProductModal
                ismuvi5={ismuvi5}
                product={muvi5prod}
                productKey={customerDetails.product[0]}
                customerDetails={customerDetails}
                // setExtendedTrialData={setExtendedTrialData}
                // setShowExtendedTrialModal={setShowExtendedTrialModal}
                // handleAccountSwitch={handleAccountSwitch}
              />
            </div>
          </Accordion>
        )}
        {/* Deals */}
        {deals && deals.length > 0 && (
          <Accordion header="Deals" targetID="deals">
            <div
              className="ag-theme-alpine"
              style={{
                height: "200px",
                width: "100%",
                padding: "0 6px",
                "--ag-row-group-indent-size": "20px",
                overflowX: "auto",
              }}
              tabIndex={0}
            >
              <AgGridReact
                className="scrollable"
                columnDefs={[
                  {
                    headerName: "ID",
                    field: "milestone_id",
                    width: 100,
                    cellRenderer: (params) => {
                      return (
                        <div style={{ alignItems: "center" }}>
                          <NavLink
                            to={{
                              pathname: `/deal/${params.data.deal.deal_id}`,
                            }}
                            target="_blank"
                          >
                            {params.value ? params.value : "NA"}
                          </NavLink>
                        </div>
                      );
                    },
                  },
                  {
                    headerName: "Deal Title",
                    field: "milestone_desc",
                    width: 150,
                    cellRenderer: (params) => {
                      return params.value ? params.value : "NA";
                    },
                  },
                  {
                    headerName: "Product",
                    field: "product",
                    width: 150,
                    cellRenderer: (params) => {
                      let product_keys = [];

                      if (Array.isArray(params.value)) {
                        product_keys = params.value;
                      } else {
                        product_keys = [params.value];
                      }

                      const productNames = [];

                      product_keys.forEach((key) => {
                        const product = productsList.find(
                          (p) => p.product_key === key,
                        );
                        if (product) {
                          productNames.push(product.value);
                        }
                      });

                      return productNames.length > 0
                        ? productNames.join(", ")
                        : "NA";
                    },
                  },
                  {
                    headerName: "Due Date",
                    field: "milestone_due",
                    width: 150,
                    cellRenderer: (params) => {
                      return (
                        getFormattedDate(params.value, "MMM, dd yyyy") || "NA"
                      );
                    },
                  },
                  {
                    headerName: "Amount ($)",
                    field: "milestone_amount",
                    width: 150,
                    cellRenderer: (params) => {
                      return params.value ? `$${params.value}` : "NA";
                    },
                  },
                  {
                    headerName: "Received On",
                    field: "milestone_receive",
                    width: 150,
                    cellRenderer: (params) => {
                      if (!params.value) {
                        return "NA";
                      }

                      return (
                        getFormattedDate(params.value, "MMM, dd yyyy") || "NA"
                      );
                    },
                  },
                  {
                    headerName: "Deal State",
                    field: "deal_state",
                    width: 150,
                    cellRenderer: (params) => {
                      return params.value ? params.value : "NA";
                    },
                  },
                  {
                    headerName: "Deal Status",
                    field: "deal_status",
                    width: 150,
                    cellRenderer: (params) => {
                      return params.data &&
                        params.data.deal &&
                        params.data.deal.deal_status ? (
                        <p className="bagde-customer">Open</p>
                      ) : (
                        <p className="badge-close">Close</p>
                      );
                    },
                  },
                  {
                    headerName: "Payment Status",
                    field: "milestone_status",
                    width: 150,
                    cellRenderer: (params) => {
                      return params.value === "Paid" ? (
                        <p className="bagde-customer">Paid</p>
                      ) : params.value === "Due" ? (
                        new Date(new Date().toLocaleDateString("en-CA")) >
                        new Date(convertUnixTimeStampToDate(params.value)) ? (
                          <p className="bagde-danger">Overdue</p>
                        ) : (
                          <p className="bagde-warning">Due</p>
                        )
                      ) : params.value === "Lost" ? (
                        <p className="bagde-internal">Lost</p>
                      ) : (
                        <p className="badge-yellow">Draft</p>
                      );
                    },
                  },
                ]}
                rowData={deals || []}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                }}
                rowHeight={40}
                suppressHorizontalScroll={false}
                theme="legacy"
                suppressContextMenu={true}
                enableCellTextSelection={true}
                enableClipboard={true}
                copyHeadersToClipboard={true}
                enableRangeSelection={true}
              />
            </div>
          </Accordion>
        )}
        {/* Contact Us */}
        {contactUs && contactUs.length > 0 && (
          <Accordion header="Contact Us" targetID="contact-us" fixedHeight={250}>
            <div className="messages-panel">
              <div className="messages-list">
                {contactUs.map((message, index) => (
                  <div className="message-container" key={index}>
                    <div className="message-title">
                      <span></span>
                      <span>
                        {getFormattedDate(
                          message.created_at,
                          "MMM, dd yyyy - hh:mm a",
                        )}
                      </span>
                    </div>
                    <div className="message-preview">{message.message}</div>
                  </div>
                ))}
              </div>
            </div>
          </Accordion>
        )}
        {/* Ask Sales */}
        {askSales && askSales.length > 0 && (
          <Accordion header="Ask Sales" targetID="ask-sales" fixedHeight={250}>
            <div className="messages-panel">
              <div className="messages-list">
                {askSales.map((message, index) => (
                  <div className="message-container" key={index}>
                    <div className="message-title">
                      <span></span>
                      <span>
                        {getFormattedDate(
                          message.created_at,
                          "MMM, dd yyyy - hh:mm a",
                        )}
                      </span>
                    </div>
                    <div className="message-preview">{message.message}</div>
                  </div>
                ))}
              </div>
            </div>
          </Accordion>
        )}
        {liveChat && liveChat.length > 0 && (
          <Accordion header="Live Chat" targetID="live-chat" fixedHeight={250}>
            <div className="messages-panel">
              <div className="messages-list">
                {liveChat.map((chat, index) => (
                  <div className="message-container" key={index}>
                    <div className="message-title">
                      <span></span>
                      <span>
                        {getFormattedDate(
                          chat.timestamp,
                          "MMM, dd yyyy - hh:mm a",
                        )}
                      </span>
                    </div>
                    <div className="message-preview">
                      <NavLink
                        to={{
                          pathname: "/chat/" + chat.customer_tattle_uuid,
                        }}
                        target="_blank"
                      >
                        {chat.preview || "NA"}
                      </NavLink>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Accordion>
        )}
        {/* Support Ticket List */}
        {ticketList && ticketList.length > 0 && (
          <Accordion
            header="Open Tickets"
            targetID="support-tickets"
            fixedHeight={250}
          >
            <div
              className="ag-theme-alpine"
              style={{
                height: "200px",
                width: "100%",
                padding: "0 6px",
                "--ag-row-group-indent-size": "20px",
                overflowX: "auto",
              }}
              tabIndex={0}
            >
              <AgGridReact
                className="scrollable"
                columnDefs={[
                  {
                    headerName: "ID",
                    field: "customer_tattle_uuid",
                    width: 100,
                    cellRenderer: (params) => {
                      return (
                        <div style={{ alignItems: "center" }}>
                          <NavLink
                            to={{
                              pathname: `${process.env.REACT_APP_tattle}/tattle/support-ticket/${params.value}`,
                            }}
                            target="_blank"
                          >
                            {params.value ? params.value : "NA"}
                          </NavLink>
                        </div>
                      );
                    },
                  },
                  // Title
                  {
                    headerName: "Title",
                    field: "title",
                    width: 200,
                    cellRenderer: (params) => {
                      return params.value || "NA";
                    },
                  },

                  // Status
                  {
                    headerName: "Status",
                    field: "status",
                    width: 150,
                    cellRenderer: (params) => {
                      if (!params.value || !columnsDef || !columnsDef.columns) {
                        return params.value || "NA";
                      }

                      // 1. Find status column
                      const statusColumn = columnsDef.columns.find(
                        function (col) {
                          return col.field === "status";
                        },
                      );

                      if (!statusColumn || !statusColumn.options) {
                        return params.value || "NA";
                      }

                      // 2. Find matching option
                      const stat = statusColumn.options.find(function (opt) {
                        return opt._id === params.value;
                      });

                      if (!stat) return "NA";

                      // 3. Resolve colors
                      const styles = hexToRgba(stat.color, 0.1);

                      return (
                        <span
                          className="status-text status_label_details"
                          style={{
                            backgroundColor: styles.backgroundColor,
                            color: styles.color,
                            border: "1px solid " + styles.borderColor,
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: 500,
                            display: "inline-block",
                          }}
                        >
                          {stat.name}
                        </span>
                      );
                    },
                  },
                  // Assigned To
                  {
                    headerName: "Assigned To",
                    field: "assign_to", // make sure this matches your row field
                    width: 200,
                    cellRenderer: function (params) {
                      if (!params.value || !usersList) {
                        return "NA";
                      }

                      // params.value = user_uuid
                      var user = usersList[params.value];
                      if (!user) {
                        return "NA";
                      }

                      var profilePic =
                        user.profile_pic ||
                        "https://ui-avatars.com/api/?name=" +
                        encodeURIComponent(user.name || "User") +
                        "&size=40&background=random";

                      return (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <img
                            src={profilePic}
                            alt={user.name}
                            className="rounded-circle rci"
                            style={{
                              width: "22px",
                              height: "22px",
                              objectFit: "cover",
                            }}
                          />
                          <span>{user.name}</span>
                        </span>
                      );
                    },
                  },

                  // Type
                  {
                    headerName: "Type",
                    field: "category",
                    width: 150,
                    cellRenderer: function (params) {
                      if (!params.value || !columnsDef || !columnsDef.columns) {
                        return "NA";
                      }

                      // 1. Find category column definition
                      var categoryColumn = columnsDef.columns.find(
                        function (col) {
                          return col.field === "category";
                        },
                      );

                      if (!categoryColumn || !categoryColumn.options) {
                        return "NA";
                      }

                      // 2. Find matching category option
                      var category = categoryColumn.options.find(
                        function (opt) {
                          return opt._id === params.value;
                        },
                      );

                      // 3. Return ONLY name (plain text)
                      return category ? category.name || "NA" : "NA";
                    },
                  },
                  // Priority
                  {
                    headerName: "Priority",
                    field: "priority",
                    width: 120,
                    cellRenderer: function (params) {
                      if (!params.value || !columnsDef || !columnsDef.columns) {
                        return "NA";
                      }

                      // 1. Find priority column definition
                      var priorityColumn = columnsDef.columns.find(function (
                        col
                      ) {
                        return col.field === "priority";
                      });

                      if (!priorityColumn || !priorityColumn.options) {
                        return "NA";
                      }

                      // 2. Find matching priority option
                      var priority = priorityColumn.options.find(function (
                        opt
                      ) {
                        return opt._id === params.value;
                      });

                      // 3. Return ONLY name (normal text)
                      return priority ? priority.option_name || "NA" : "NA";
                    },
                  },
                  // Product
                  {
                    headerName: "Product",
                    field: "product",
                    width: 150,
                    cellRenderer: (params) => {
                      let product_keys = [];

                      if (Array.isArray(params.value)) {
                        product_keys = params.value;
                      } else {
                        product_keys = [params.value];
                      }

                      const productNames = [];

                      product_keys.forEach((key) => {
                        const product = productsList.find(
                          (p) => p.product_key === key,
                        );
                        if (product) {
                          productNames.push(product.value);
                        }
                      });

                      return productNames.length > 0
                        ? productNames.join(", ")
                        : "NA";
                    },
                  },
                  // Created On
                  {
                    headerName: "Created On",
                    field: "created_date",
                    width: 150,
                    cellRenderer: (params) => {
                      return params.value ? params.value : "NA";
                    },
                  },
                ]}
                rowData={ticketList || []}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                }}
                rowHeight={40}
                suppressHorizontalScroll={false}
                theme="legacy"
                suppressContextMenu={true}
                enableCellTextSelection={true}
                enableClipboard={true}
                copyHeadersToClipboard={true}
                enableRangeSelection={true}
              />
            </div>
          </Accordion>
        )}
      </div>
      <ExtendedFreeTrial
        show={showExtendedTrialModal}
        data={extendedTrialData}
        close={() => setShowExtendedTrialModal(false)}
        syncFilterWithAggrid={() => { }}
        fetchSubscriptionDetails={fetchSubscriptionDetails}
      />
      {
        // switchAccountData && (
        <ConfirmationModal
          title="Confirm Account Move"
          text={`Are you sure move this account to ${switchAccountData && switchAccountData.is_demo ? "Demo" : "Lead"
            }?`}
          onConfirm={() => handleConfirmAccountSwitch()}
          showModal={showSwitchToDemoModal}
          setShowModal={setShowSwitchToDemoModal}
        />
        // )
      }
    </div>
  );
};

export default OverviewPanel;



// WEBPACK FOOTER //
// ./src/components/crm/leads/components/OverviewPanel.js